import React, { useState, useEffect } from 'react';

const EventOrganizers = ({ eventId: propEventId }) => {
  const [organizers, setOrganizers] = useState([]);
  const [roleTemplates, setRoleTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('Co-organisateur');
  const [customPermissions, setCustomPermissions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const getEventId = () => {
    if (propEventId) return propEventId;
    const urlPath = window.location.pathname;
    const match = urlPath.match(/\/events\/(\d+)/);
    return match ? match[1] : null;
  };

  const eventId = getEventId();

  useEffect(() => {
    if (eventId) {
      loadOrganizers();
    }
  }, [eventId]);

  const loadOrganizers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/events/${eventId}/organizers`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const data = await response.json();
      if (data.success) {
        setOrganizers(data.organizers);
        setRoleTemplates(data.available_role_templates);
      }
    } catch (error) {
      console.error('Erreur chargement organisateurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchMembers = async (q) => {
    setSearchTerm(q);
    if (q.length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const res = await fetch(`/api/v1/users?search=${encodeURIComponent(q)}&per_page=50&status=active`, {
        headers: { 'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content }
      });
      const data = await res.json();
      const existingIds = new Set(organizers.map(o => o.user?.id));
      setSearchResults((data.users || data.data?.users || []).filter(u => !existingIds.has(u.id)));
    } catch { setSearchResults([]); } finally { setSearchLoading(false); }
  };

  const handleAddOrganizer = async () => {
    if (!selectedUser) {
      alert('Veuillez sélectionner un utilisateur');
      return;
    }

    try {
      const permissions = Object.keys(customPermissions).length > 0 
        ? customPermissions 
        : roleTemplates[selectedRole];

      const response = await fetch(`/api/v1/events/${eventId}/organizers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
          role_name: selectedRole,
          permissions: permissions
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('✅ Organisateur ajouté avec succès');
        setShowAddModal(false);
        setSelectedUser(null);
        setCustomPermissions({});
        setSearchTerm('');
        setSearchResults([]);
        loadOrganizers();
      } else {
        alert(`❌ ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur ajout organisateur:', error);
      alert('❌ Erreur lors de l\'ajout');
    }
  };

  const handleRemoveOrganizer = async (organizerId) => {
    if (!confirm('Êtes-vous sûr de vouloir retirer cet organisateur ?')) return;

    try {
      const response = await fetch(`/api/v1/events/${eventId}/organizers/${organizerId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const data = await response.json();
      if (data.success) {
        alert('✅ Organisateur retiré');
        loadOrganizers();
      } else {
        alert(`❌ ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('❌ Erreur lors de la suppression');
    }
  };

  const organizerIds = new Set(organizers.map(o => o.user?.id));

  if (loading) {
    return (
      <div style={{ padding: '160px 40px 40px', minHeight: '100vh', background: '#f7eef2' }}>
        <div style={{ color: '#fff', textAlign: 'center', fontSize: '1.2rem' }}>
          Chargement...
        </div>
      </div>
    );
  }

  return (
    <div className="event-mobile-shell event-organizers-page" style={{ padding: '160px 40px 40px', minHeight: '100vh', background: '#f7eef2' }}>
      <div className="event-mobile-container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div className="event-page-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '40px'
        }}>
          <div>
            <h1 style={{
              color: '#fff',
              fontSize: 'clamp(2rem, 4vw, 2.5rem)',
              fontWeight: '800',
              marginBottom: '8px'
            }}>
              Organisateurs
            </h1>
            <p style={{ color: '#9e6575', fontSize: '1rem' }}>
              Gérez les membres de votre équipe d'organisation
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              background: 'linear-gradient(135deg, #7c2837 0%, #943143 50%, #a8384e 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              padding: '14px 28px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(124, 40, 55, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(124, 40, 55, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(124, 40, 55, 0.3)';
            }}
          >
            ➕ Ajouter un organisateur
          </button>
        </div>

        {/* Liste des organisateurs */}
        <div style={{ display: 'grid', gap: '20px' }}>
          {organizers.map(org => (
            <div
              key={org.id}
              style={{
                background: '#ffffff',
                borderRadius: '16px',
                padding: '24px',
                border: '1px solid rgba(124, 40, 55, 0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(124, 40, 55, 0.5)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(124, 40, 55, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div className="event-organizer-card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  {/* User info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #7c2837 0%, #943143 50%, #a8384e 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: '700',
                      fontSize: '1.2rem'
                    }}>
                      {org.user.name.charAt(0)}
                    </div>
                    <div>
                      <h3 style={{ color: '#fff', fontSize: '1.3rem', fontWeight: '700', marginBottom: '4px' }}>
                        {org.user.name}
                        {org.is_primary && (
                          <span style={{
                            marginLeft: '12px',
                            padding: '4px 12px',
                            background: 'linear-gradient(135deg, #7c2837 0%, #943143 50%, #a8384e 100%)',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            👑 Principal
                          </span>
                        )}
                      </h3>
                      <p style={{ color: '#9e6575', fontSize: '0.9rem' }}>{org.user.email}</p>
                    </div>
                  </div>

                  {/* Rôle */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{
                      display: 'inline-block',
                      background: 'rgba(124, 40, 55, 0.2)',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid rgba(124, 40, 55, 0.4)'
                    }}>
                      <span style={{ color: '#943143', fontWeight: '600', fontSize: '0.95rem' }}>
                        {org.role_name}
                      </span>
                    </div>
                    {org.role_description && (
                      <p style={{ color: '#9e6575', fontSize: '0.9rem', marginTop: '8px' }}>
                        {org.role_description}
                      </p>
                    )}
                  </div>

                  {/* Permissions */}
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '8px',
                    marginTop: '12px'
                  }}>
                    {Object.entries(org.permissions).filter(([_, value]) => value).map(([key, _]) => (
                      <span
                        key={key}
                        style={{
                          background: '#fdf6f8',
                          color: '#15803d',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          border: '1px solid rgba(34, 197, 94, 0.2)'
                        }}
                      >
                        ✓ {key.replace('can_', '').replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>

                  {/* Ajouté par */}
                  {org.added_by && (
                    <p style={{ color: '#888888', fontSize: '0.85rem', marginTop: '12px' }}>
                      Ajouté par {org.added_by.name} • {new Date(org.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>

                {/* Actions */}
                {!org.is_primary && (
                  <button
                    onClick={() => handleRemoveOrganizer(org.id)}
                    style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      color: '#ef4444',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                    }}
                  >
                    🗑️ Retirer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Modal Ajout */}
        {showAddModal && (
          <div className="event-detail-modal" style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(44, 14, 22, 0.7)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '24px',
              padding: '36px',
              maxWidth: '560px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '2px solid rgba(124, 40, 55, 0.15)',
              boxShadow: '0 20px 60px rgba(44, 14, 22, 0.25)'
            }}>
              <h2 style={{ color: '#2c0e16', fontSize: '1.4rem', fontWeight: 800, marginBottom: '6px' }}>
                👥 Ajouter un organisateur
              </h2>
              <p style={{ color: '#9e6575', fontSize: '0.88rem', marginBottom: '24px' }}>Recherchez un adhérent par nom ou email</p>

              {/* Recherche autocomplete */}
              <div style={{ marginBottom: '20px', position: 'relative' }}>
                <label style={{ color: '#7a4050', display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '0.9rem' }}>
                  Rechercher un adhérent
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => searchMembers(e.target.value)}
                  placeholder="Tapez un nom ou email…"
                  autoComplete="off"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: '#fdf6f8',
                    border: '1.5px solid rgba(124, 40, 55, 0.18)',
                    borderRadius: '10px',
                    color: '#2c0e16',
                    fontSize: '0.95rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                {searchLoading && (
                  <span style={{ position: 'absolute', right: 14, top: 42, color: '#9e6575', fontSize: '0.85rem' }}>⏳</span>
                )}

                {/* Résultats dropdown */}
                {searchTerm.length >= 2 && !searchLoading && searchResults.length === 0 && !selectedUser && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: '#fff', border: '1px solid rgba(124,40,55,0.15)', borderRadius: '0 0 10px 10px',
                    padding: '12px 16px', fontSize: '0.88rem', color: '#9e6575',
                    boxShadow: '0 6px 20px rgba(44,14,22,0.10)'
                  }}>
                    Aucun adhérent trouvé pour « {searchTerm} »
                  </div>
                )}
                {searchResults.length > 0 && !selectedUser && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: '#fff', border: '1px solid rgba(124,40,55,0.18)', borderRadius: '0 0 12px 12px',
                    boxShadow: '0 8px 24px rgba(44,14,22,0.13)', overflow: 'hidden', maxHeight: '240px', overflowY: 'auto'
                  }}>
                    {searchResults.map((user, idx) => (
                      <div
                        key={user.id}
                        onClick={() => { setSelectedUser(user); setSearchResults([]); setSearchTerm(user.full_name || `${user.first_name} ${user.last_name}`); }}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '11px 16px', cursor: 'pointer',
                          borderBottom: idx < searchResults.length - 1 ? '1px solid rgba(124,40,55,0.07)' : 'none',
                          transition: 'background 0.12s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#faf0f3'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                      >
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 600, color: '#2c0e16', fontSize: '0.92rem' }}>{user.full_name || `${user.first_name} ${user.last_name}`}</div>
                          <div style={{ fontSize: '0.8rem', color: '#9e6575' }}>{user.email}</div>
                        </div>
                        <span style={{ color: '#7c2837', fontSize: '0.8rem', fontWeight: 600, marginLeft: 12 }}>→ Sélectionner</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Utilisateur sélectionné */}
              {selectedUser && (
                <div style={{
                  background: '#f0fdf4', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '10px',
                  padding: '12px 16px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: 700, color: '#15803d', fontSize: '0.92rem' }}>✓ {selectedUser.full_name || `${selectedUser.first_name} ${selectedUser.last_name}`}</div>
                    <div style={{ fontSize: '0.8rem', color: '#166534' }}>{selectedUser.email}</div>
                  </div>
                  <button onClick={() => { setSelectedUser(null); setSearchTerm(''); }} style={{
                    background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem'
                  }}>✕</button>
                </div>
              )}

              {/* Sélection rôle */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: '#7a4050', display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '0.9rem' }}>
                  Rôle
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: '#fdf6f8',
                    border: '1.5px solid rgba(124, 40, 55, 0.18)',
                    borderRadius: '10px',
                    color: '#2c0e16',
                    fontSize: '0.95rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                >
                  {Object.keys(roleTemplates).filter(role => role !== 'Organisateur principal').map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              {/* Permissions preview */}
              {selectedRole && roleTemplates[selectedRole] && (
                <div style={{
                  background: '#fdf6f8',
                  padding: '16px',
                  borderRadius: '10px',
                  marginBottom: '24px'
                }}>
                  <h4 style={{ color: '#7a4050', fontSize: '0.85rem', fontWeight: 700, marginBottom: '10px' }}>
                    Permissions incluses :
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {Object.entries(roleTemplates[selectedRole]).filter(([_, value]) => value).map(([key, _]) => (
                      <span
                        key={key}
                        style={{
                          background: '#f0fdf4',
                          color: '#15803d',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          border: '1px solid rgba(34, 197, 94, 0.2)'
                        }}
                      >
                        ✓ {key.replace('can_', '').replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedUser(null);
                    setCustomPermissions({});
                    setSearchTerm('');
                    setSearchResults([]);
                  }}
                  style={{
                    background: '#fdf6f8',
                    border: '1.5px solid rgba(124, 40, 55, 0.2)',
                    color: '#7a4050',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: 700
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleAddOrganizer}
                  disabled={!selectedUser}
                  style={{
                    background: selectedUser ? 'linear-gradient(135deg, #7c2837, #a8384e)' : '#ccc',
                    border: 'none',
                    color: '#fff',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    cursor: selectedUser ? 'pointer' : 'not-allowed',
                    fontSize: '0.95rem',
                    fontWeight: 700
                  }}
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventOrganizers;
